import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, FileText, History, Loader2, Pencil, ShieldCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { CommitmentEditor, CommitmentText, openCommitmentPdf } from '../components/isg/CommitmentText';
import { writeStyledExcel } from '../lib/styledExcel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' }) : '-');

const EditDialog = ({ loc, onClose, onSaved }) => {
  const cur = loc.current;
  const [title, setTitle] = useState(cur?.title || `${loc.location_name} İSG Taahhütnamesi`);
  const [content, setContent] = useState(cur?.content || '');
  const [file, setFile] = useState(null);
  const [keepPdf, setKeepPdf] = useState(!!cur?.pdf_name);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const body = new FormData();
    body.append('title', title); body.append('content', content); body.append('keep_pdf', keepPdf && !file);
    if (file) body.append('file', file);
    setBusy(true);
    try {
      const r = await axios.post(`${API}/isg-commitments/${loc.location_id}`, body);
      toast.success(`Sürüm ${r.data.version} yayınlandı`, { description: 'Tüm firma kullanıcılarına bir kez yeniden onaylatılacak.' });
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Kaydedilemedi'); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-5xl max-h-[94vh] overflow-y-auto" data-testid="commitment-edit-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle>{loc.location_name} – İSG Taahhütnamesi {cur ? `(yeni sürüm: ${cur.version + 1})` : '(Sürüm 1)'}</DialogTitle></DialogHeader>
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="space-y-3">
            <div><Label>Başlık</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" data-testid="commitment-title-input" /></div>
            <div><Label>Metin</Label><div className="mt-1"><CommitmentEditor value={content} onChange={setContent} /></div></div>
            <div className="space-y-1.5 rounded-lg border bg-slate-50 p-3">
              <Label>PDF Eki (opsiyonel)</Label>
              <Input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="commitment-pdf-input" />
              {cur?.pdf_name && !file && (
                <label className="flex items-center gap-2 text-xs text-slate-600"><input type="checkbox" checked={keepPdf} onChange={(e) => setKeepPdf(e.target.checked)} data-testid="commitment-keep-pdf" />Mevcut eki koru: {cur.pdf_name}</label>
              )}
            </div>
          </div>
          <div><Label>Önizleme (firmanın göreceği)</Label><div className="mt-1 max-h-[60vh] overflow-y-auto rounded-lg border bg-white p-4"><h2 className="mb-2 font-bold">{title}</h2><CommitmentText content={content} /></div></div>
        </div>
        {cur && <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">Kaydettiğinizde yeni sürüm yayınlanır; Sürüm {cur.version} ve onay kayıtları geçmişte saklanır, firmalar yeni sürümü bir kez yeniden onaylar.</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Vazgeç</Button>
          <Button onClick={save} disabled={busy || !content.trim() || !title.trim()} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="commitment-save-btn">{busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Yayınla</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const HistoryDialog = ({ loc, onClose }) => {
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { axios.get(`${API}/isg-commitments/${loc.location_id}/versions`).then(r => { setList(r.data); setOpen(r.data[0]?.id); }); }, [loc.location_id]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="commitment-history-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle>{loc.location_name} – Sürüm Geçmişi</DialogTitle></DialogHeader>
        {!list ? <Loader2 className="h-5 w-5 animate-spin" /> : list.map(v => (
          <div key={v.id} className="rounded-lg border" data-testid={`commitment-version-${v.version}`}>
            <button type="button" onClick={() => setOpen(open === v.id ? null : v.id)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm">
              <Badge className={v.is_current ? 'bg-emerald-600' : 'bg-slate-400'}>Sürüm {v.version}</Badge>
              <span className="font-semibold">{v.title}</span><span className="ml-auto text-xs text-slate-500">{fmt(v.created_at)} · {v.created_by}</span>
            </button>
            {open === v.id && (
              <div className="border-t bg-slate-50 p-3"><CommitmentText content={v.content} />
                {v.pdf_name && <Button size="sm" variant="outline" className="mt-2" onClick={() => openCommitmentPdf(v.id)}><FileText className="mr-1 h-4 w-4" />{v.pdf_name}</Button>}</div>
            )}
          </div>
        ))}
      </DialogContent>
    </Dialog>
  );
};

const downloadCert = (r) => axios.get(`${API}/isg-commitments/acceptances/${r.id}/pdf`, { responseType: 'blob' }).then(res => {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(res.data);
  a.download = `taahhut_onay_${(r.company_name || '').replace(/\s+/g, '_')}_v${r.version}.pdf`;
  a.click();
}).catch(() => toast.error('Belge oluşturulamadı'));

const PendingList = ({ locations }) => {
  const withCommitment = locations.filter(l => l.current);
  const [loc, setLoc] = useState('');
  const [all, setAll] = useState(false);
  const [data, setData] = useState(null);
  const locId = loc || withCommitment[0]?.location_id;
  useEffect(() => { if (locId) { setData(null); axios.get(`${API}/isg-commitments/${locId}/pending-users`, { params: { include_no_permit: all } }).then(r => setData(r.data)); } }, [locId, all]);
  const rows = data?.users || [];
  const locName = withCommitment.find(l => l.location_id === locId)?.location_name;
  const exportXlsx = () => writeStyledExcel({
    fileName: 'isg_taahhutname_onaylamayanlar.xlsx', sheetName: 'Onaylamayanlar', title: `${locName} – Taahhütnameyi Onaylamayanlar (Sürüm ${data?.commitment?.version})`, subtitle: `${rows.length} kullanıcı · ${new Date().toLocaleString('tr-TR')}`,
    columns: [{ header: 'Firma', key: 'company_name', width: 30 }, { header: 'Kullanıcı', key: 'user_name', width: 22 }, { header: 'E-posta', key: 'user_email', width: 28 }, { header: 'Lokasyonda Talebi', key: 'tp', width: 16 }, { header: 'Daha Önce Onayladığı', key: 'lv', width: 22 }],
    rows: rows.map(r => ({ ...r, tp: r.has_permit ? 'Var' : 'Yok', lv: r.last_version ? `Sürüm ${r.last_version} (${fmt(r.last_accepted_at)})` : 'Hiç onaylamadı' })),
  });
  if (!withCommitment.length) return <p className="rounded-lg border bg-white p-6 text-center text-slate-400">Henüz taahhütname tanımlı lokasyon yok</p>;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <select value={locId} onChange={(e) => setLoc(e.target.value)} className="h-9 rounded-md border bg-white px-2 text-sm" data-testid="pending-location-filter">
          {withCommitment.map(l => <option key={l.location_id} value={l.location_id}>{l.location_name} (Sürüm {l.current.version})</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} data-testid="pending-include-no-permit" />Bu lokasyonda talebi olmayan yetkili firmaları da göster</label>
        {data && <Badge className="bg-rose-100 text-rose-700" data-testid="pending-count">{rows.length} kullanıcı onaylamadı</Badge>}
        <Button size="sm" variant="outline" className="ml-auto" onClick={exportXlsx} disabled={!rows.length} data-testid="pending-export-btn"><Download className="mr-1 h-4 w-4" />Excel'e Aktar</Button>
      </div>
      <div className="overflow-x-auto rounded-lg border bg-white">
        <table className="w-full text-sm" data-testid="pending-table">
          <thead className="bg-slate-50 text-xs text-slate-600"><tr>{['Firma', 'Kullanıcı', 'Lokasyonda Talebi', 'Daha Önce Onayladığı'].map(h => <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>)}</tr></thead>
          <tbody>
            {!data ? <tr><td colSpan={4} className="p-4"><Loader2 className="h-5 w-5 animate-spin" /></td></tr>
              : !rows.length ? <tr><td colSpan={4} className="p-6 text-center text-emerald-600">Herkes güncel sürümü onaylamış</td></tr>
              : rows.map(r => (
                <tr key={r.user_id} className="border-t" data-testid={`pending-row-${r.user_id}`}>
                  <td className="px-3 py-2 font-medium">{r.company_name || '-'}</td>
                  <td className="px-3 py-2">{r.user_name}<div className="text-xs text-slate-400">{r.user_email}</div></td>
                  <td className="px-3 py-2">{r.has_permit ? <Badge variant="outline" className="border-sky-300 text-sky-700">Var</Badge> : <Badge variant="outline" className="text-slate-400">Yok</Badge>}</td>
                  <td className="px-3 py-2">{r.last_version ? <span>Sürüm {r.last_version} <span className="text-xs text-slate-400">({fmt(r.last_accepted_at)})</span></span> : <span className="text-rose-600">Hiç onaylamadı</span>}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AcceptanceList = ({ locations }) => {
  const [rows, setRows] = useState(null);
  const [loc, setLoc] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => { axios.get(`${API}/isg-commitments/acceptances`, { params: loc ? { location_id: loc } : {} }).then(r => setRows(r.data)); }, [loc]);
  const shown = useMemo(() => (rows || []).filter(r => !q || `${r.company_name} ${r.user_name} ${r.request_number}`.toLocaleLowerCase('tr').includes(q.toLocaleLowerCase('tr'))), [rows, q]);
  const exportXlsx = () => writeStyledExcel({
    fileName: 'isg_taahhutname_onaylari.xlsx', sheetName: 'Onaylar', title: 'İSG Taahhütnamesi Onayları', subtitle: `${shown.length} kayıt · ${new Date().toLocaleString('tr-TR')}`,
    columns: [{ header: 'Firma', key: 'company_name', width: 30 }, { header: 'Kullanıcı', key: 'user_name', width: 22 }, { header: 'E-posta', key: 'user_email', width: 26 }, { header: 'Lokasyon', key: 'location_name', width: 22 }, { header: 'Sürüm', key: 'version', width: 8 }, { header: 'Tarih / Saat', key: 'at', width: 18 }, { header: 'Talep No', key: 'request_number', width: 12 }],
    rows: shown.map(r => ({ ...r, at: fmt(r.accepted_at) })),
  });
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <select value={loc} onChange={(e) => setLoc(e.target.value)} className="h-9 rounded-md border bg-white px-2 text-sm" data-testid="acceptance-location-filter">
          <option value="">Tüm lokasyonlar</option>{locations.map(l => <option key={l.location_id} value={l.location_id}>{l.location_name}</option>)}
        </select>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Firma, kullanıcı veya talep no ara" className="h-9 w-72" data-testid="acceptance-search" />
        <Button size="sm" variant="outline" className="ml-auto" onClick={exportXlsx} disabled={!shown.length} data-testid="acceptance-export-btn"><Download className="mr-1 h-4 w-4" />Excel'e Aktar</Button>
      </div>
      <div className="overflow-x-auto rounded-lg border bg-white">
        <table className="w-full text-sm" data-testid="acceptance-table">
          <thead className="bg-slate-50 text-xs text-slate-600"><tr>{['Firma', 'Kullanıcı', 'Lokasyon', 'Sürüm', 'Tarih / Saat', 'Talep No', 'Belge'].map(h => <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>)}</tr></thead>
          <tbody>
            {rows === null ? <tr><td colSpan={7} className="p-4"><Loader2 className="h-5 w-5 animate-spin" /></td></tr>
              : !shown.length ? <tr><td colSpan={7} className="p-6 text-center text-slate-400">Henüz onay kaydı yok</td></tr>
              : shown.map(r => (
                <tr key={r.id} className="border-t" data-testid={`acceptance-row-${r.id}`}>
                  <td className="px-3 py-2 font-medium">{r.company_name || '-'}</td><td className="px-3 py-2">{r.user_name}<div className="text-xs text-slate-400">{r.user_email}</div></td>
                  <td className="px-3 py-2">{r.location_name}</td><td className="px-3 py-2"><Badge variant="outline">v{r.version}</Badge></td>
                  <td className="px-3 py-2 tabular-nums">{fmt(r.accepted_at)}</td><td className="px-3 py-2 font-mono">{r.request_number ? `#${String(r.request_number).padStart(4, '0')}` : <span className="text-slate-400">Taslak</span>}</td>
                  <td className="px-3 py-2"><Button size="sm" variant="outline" className="h-7" onClick={() => downloadCert(r)} data-testid={`acceptance-pdf-btn-${r.id}`}><FileText className="mr-1 h-3.5 w-3.5" />Belge</Button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function IsgCommitmentsPage() {
  const { user, canView, canTab } = useAuth();
  const [locs, setLocs] = useState([]);
  const [edit, setEdit] = useState(null);
  const [hist, setHist] = useState(null);
  const load = () => axios.get(`${API}/isg-commitments`).then(r => setLocs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const admin = canTab('isg_commitments', 'list');
  return (
    <div className="space-y-6" data-testid="isg-commitments-page">
      <div><h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900"><ShieldCheck className="h-6 w-6 text-emerald-600" />İSG Taahhütnameleri</h1>
        <p className="mt-1 text-slate-500">Lokasyon bazlı taahhütnameler; firma kullanıcıları talep oluştururken bir kez okuyup onaylar.</p></div>
      <Tabs defaultValue={['list', 'acc', 'pending'].find(t => canTab('isg_commitments', t))}>
        <TabsList>{admin && <TabsTrigger value="list" data-testid="tab-commitments">Taahhütnameler</TabsTrigger>}{canTab('isg_commitments', 'acc') && <TabsTrigger value="acc" data-testid="tab-acceptances">Onaylayanlar</TabsTrigger>}{canTab('isg_commitments', 'pending') && <TabsTrigger value="pending" data-testid="tab-pending">Onaylamayanlar</TabsTrigger>}</TabsList>
        {admin && (
          <TabsContent value="list" className="space-y-2">
            {locs.map(l => (
              <div key={l.location_id} className="flex flex-wrap items-center gap-3 rounded-lg border bg-white px-4 py-3" data-testid={`commitment-loc-${l.location_id}`}>
                <div className="min-w-[200px]"><p className="font-semibold text-slate-900">{l.location_name}</p>
                  <p className="text-xs text-slate-500">{l.current ? `${l.current.title} · Sürüm ${l.current.version} · ${fmt(l.current.created_at)}` : 'Taahhütname tanımlı değil (firmaya sorulmaz)'}</p></div>
                {l.current && <Badge className="bg-emerald-100 text-emerald-800">{l.accepted_count} onay</Badge>}
                <div className="ml-auto flex gap-2">
                  {l.current && <Button size="sm" variant="outline" onClick={() => setHist(l)} data-testid={`commitment-history-btn-${l.location_id}`}><History className="mr-1 h-4 w-4" />Geçmiş</Button>}
                  <Button size="sm" onClick={() => setEdit(l)} data-testid={`commitment-edit-btn-${l.location_id}`}><Pencil className="mr-1 h-4 w-4" />{l.current ? 'Güncelle' : 'Oluştur'}</Button>
                </div>
              </div>
            ))}
          </TabsContent>
        )}
        <TabsContent value="acc"><AcceptanceList locations={locs} /></TabsContent>
        <TabsContent value="pending"><PendingList locations={locs} /></TabsContent>
      </Tabs>
      {edit && <EditDialog loc={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      {hist && <HistoryDialog loc={hist} onClose={() => setHist(null)} />}
    </div>
  );
}
